/**
 * TextInputStep — textarea for pasting campaign text + file upload (.txt, .md).
 * FR-001, FR-002, FR-003
 */

import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useI18n } from '../../i18n';

interface TextInputStepProps {
  onSubmit: (text: string) => void;
  onBack: () => void;
  onCancel: () => void;
  isLoading: boolean;
}

const MAX_TEXT_LENGTH = 200_000;

export function TextInputStep({ onSubmit, onBack, onCancel, isLoading }: TextInputStepProps) {
  const { t } = useI18n();
  const [text, setText] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const content = reader.result as string;
      setText(content);
    };
    reader.readAsText(file, 'utf-8');
  };

  const charCount = text.length;
  const isOverLimit = charCount > MAX_TEXT_LENGTH;
  const canSubmit = charCount > 0 && !isOverLimit && !isLoading;

  return (
    <div className="space-y-4">
      {/* [STYLE_TODO] Text input area with proper sizing */}
      <p className="text-ui text-muted-foreground">
        {t('campaignImport.textInputHint')}
      </p>

      <textarea
        className="w-full min-h-[200px] max-h-[400px] p-3 text-ui border rounded resize-y bg-background"
        placeholder={t('campaignImport.placeholder')}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />

      <div className="flex items-center gap-3">
        {/* [STYLE_TODO] File upload button styling */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
        >
          {t('campaignImport.uploadFile')}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".txt,.md,text/plain,text/markdown"
          className="hidden"
          onChange={handleFileUpload}
        />

        <span className={`text-meta ml-auto ${isOverLimit ? 'text-destructive' : 'text-muted-foreground'}`}>
          {charCount.toLocaleString()} / {MAX_TEXT_LENGTH.toLocaleString()}
        </span>
      </div>

      {isOverLimit && (
        <p className="text-ui text-destructive">
          {t('campaignImport.limitExceeded')}
        </p>
      )}

      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={onBack}>{t('common.back')}</Button>
        <Button variant="outline" onClick={onCancel}>{t('common.cancel')}</Button>
        <Button disabled={!canSubmit} onClick={() => onSubmit(text)}>
          {isLoading ? t('campaignImport.analyzing') : t('campaignImport.analyze')}
        </Button>
      </div>
    </div>
  );
}
